import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Plus, History, X } from 'lucide-react';
import { useAgent } from '../contexts/AgentContext';
import { useAgentChat, type ViewSuggestion } from '../hooks/useAgentChat';
import { useNavigateToView } from '../hooks/useNavigateToView';
import { tokenManager } from '../lib/api';
import AgentChatView from '../components/AgentChatView';

interface ConversationListItem {
  id: string;
  title: string;
  message_count: number;
  updated_at: string;
  program_id?: number;
  program_name?: string;
}

const ProgramAgentFullPage: React.FC = () => {
  const {
    pageContext,
    conversationId,
    setConversationId,
    startNewConversation: startNewConversationInContext,
    buildContextPayload,
  } = useAgent();

  const navigateToView = useNavigateToView();

  const hasLoadedRef = useRef(false);
  const [conversationHistory, setConversationHistory] = useState<ConversationListItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const {
    messages,
    isStreaming,
    viewSuggestions,
    actionSuggestions,
    pendingJobs,
    uploads,
    sendMessage,
    setUploadProgramContext,
    undo,
    dismissSuggestion,
    dismissActionSuggestion,
    loadConversation,
    startNewConversation,
  } = useAgentChat({
    conversationId,
    buildContextPayload,
    onViewSuggestion: (suggestion: ViewSuggestion) => {
      navigateToView(suggestion);
    },
    onError: (error: string) => {
      console.error('Agent error:', error);
    },
    onConversationCreated: (newConversationId: string) => {
      setConversationId(newConversationId);
    },
  });

  // Load most recent program-scoped conversation on mount (once).
  // Matches the drawer's behavior so the full page picks up where the drawer left off.
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadMostRecent = async () => {
      // Skip if we already have an active conversation (e.g. expanded from drawer)
      if (messages.length > 0 && conversationId) return;

      try {
        const token = tokenManager.getAccessToken();
        const programParam = pageContext.programId
          ? `program_id=${pageContext.programId}&`
          : '';

        const response = await fetch(
          `${import.meta.env.VITE_API_URL || ''}/api/chat/conversations?${programParam}limit=1&ordering=-updated_at`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) return;

        const data = await response.json();
        if (data.results && data.results.length > 0) {
          const mostRecent = data.results[0];
          setConversationId(mostRecent.id);
          await loadConversation(mostRecent.id);
        }
      } catch (error) {
        console.error('Error loading recent conversation:', error);
      }
    };

    loadMostRecent();
  }, [pageContext.programId]);

  // Load conversation history filtered by program
  useEffect(() => {
    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const token = tokenManager.getAccessToken();
        const programParam = pageContext.programId
          ? `?program_id=${pageContext.programId}`
          : '';

        const response = await fetch(
          `${import.meta.env.VITE_API_URL || ''}/api/chat/conversations${programParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.ok) {
          const data = await response.json();
          setConversationHistory(data.results || []);
        }
      } catch (error) {
        console.error('Failed to load conversation history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [pageContext.programId]);

  const handleNewConversation = () => {
    startNewConversationInContext();
    startNewConversation();
    setShowHistory(false);
  };

  const handleSelectConversation = (convId: string) => {
    setConversationId(convId);
    loadConversation(convId);
    setShowHistory(false);
  };

  const handleUndo = async (editId: string) => {
    try {
      await undo(editId);
    } catch (error) {
      console.error('Failed to undo:', error);
    }
  };

  const handleSend = (text: string, files?: File[]) => {
    sendMessage(text, undefined, undefined, files);
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative">
      {/* Unified single-line header: title | buttons */}
      <div className="flex items-center gap-6 px-8 py-3 border-b border-border bg-white/60 dark:bg-slate-900/40 shrink-0">
        <div className="flex items-center gap-3 shrink-0">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-foreground whitespace-nowrap">
            {pageContext.programName ? `Agent — ${pageContext.programName}` : 'Program Agent'}
          </h1>
          <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs font-bold rounded">
            AI
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          <button
            onClick={() => setShowHistory((prev) => !prev)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            title="Conversation History"
          >
            <History className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          {conversationId && (
            <button
              onClick={handleNewConversation}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title="New Conversation"
            >
              <Plus className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {/* Inline history panel */}
      {showHistory && (
        <div className="border-b border-border bg-white dark:bg-slate-900 px-8 py-4 max-h-80 overflow-y-auto shrink-0">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {pageContext.programId ? 'Program Conversations' : 'All Conversations'}
              </h3>
              <button
                onClick={() => setShowHistory(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {isLoadingHistory ? (
              <div className="text-center py-4 text-sm text-slate-500">Loading...</div>
            ) : conversationHistory.length === 0 ? (
              <div className="text-center py-4 text-sm text-slate-500">No conversations yet</div>
            ) : (
              <div className="space-y-2">
                {conversationHistory.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      conversationId === conv.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                      {conv.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
                      <span>{conv.message_count} messages</span>
                      <span>•</span>
                      <span>{new Date(conv.updated_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <AgentChatView
        variant="full"
        messages={messages}
        isStreaming={isStreaming}
        viewSuggestions={viewSuggestions}
        actionSuggestions={actionSuggestions}
        pendingJobs={pendingJobs}
        uploads={uploads}
        onToggleUploadContext={setUploadProgramContext}
        onSend={handleSend}
        onUndo={handleUndo}
        onDismissSuggestion={dismissSuggestion}
        onDismissActionSuggestion={dismissActionSuggestion}
        onNavigateToView={navigateToView}
      />
    </div>
  );
};

export default ProgramAgentFullPage;
